/**
 * F 331 — THE TWO LEVERS.
 *
 * A dashboard that only reads is a report. These are the two things the user can
 * DO from it, and both write to the graph rather than to component state, so
 * both survive a reload and both are visible to F 330's classifier.
 *
 * 1. THE POWER-UP LEVER — spend money for 24 hours.
 * 2. THE INVESTIGATE LOOP — ask a model what a provider offers.
 *
 * AND THE EMPTY SLOTS, which did not exist.
 */

import type { CapabilityName, ProviderSlot, SlotStatus } from './engineState';

// ── the slots ───────────────────────────────────────────────────────────────

/**
 * F 331 — THE EMPTY SLOTS.
 *
 * The dispatch flagged this as an assumption to check: "F 328 built the
 * credential store with empty slots (check if built or still to be added)".
 *
 * **Checked: they were NOT built.** `grep -rn "not_connected|emptySlot"` across
 * `src/` returns nothing. The credential store only ever holds credentials that
 * EXIST, so a user with no Z.ai key sees no Z.ai row at all — and cannot tell
 * the difference between "Z.ai has nothing for me" and "we never looked".
 *
 * So the slots are declared here, as DATA, separate from the credential store.
 * That separation is the point: a slot is the ABSENCE of a credential, and
 * putting it inside the credential store would mean creating fake credentials
 * to represent a hole.
 *
 * `unlocks` is left EMPTY where we have not measured what a provider offers. An
 * empty array means "we do not know", which the pie renders as grey. It is not
 * the same as an empty list meaning "it offers nothing", and the distinction is
 * the whole reason these are declarations rather than records.
 */
export interface ProviderDeclaration {
  provider: string;
  label: string;
  /** True only when a probe actually established what this offers. */
  known: boolean;
  unlocks: CapabilityName[];
  signupUrl?: string;
  note?: string;
}

export const KNOWN_PROVIDERS: ProviderDeclaration[] = [
  { provider: 'groq', label: 'Groq', known: true, unlocks: ['text', 'reasoning'], signupUrl: 'https://console.groq.com/keys' },
  { provider: 'gemini', label: 'Google Gemini', known: true, unlocks: ['text', 'vision'], signupUrl: 'https://aistudio.google.com/apikey' },
  { provider: 'nvidia', label: 'NVIDIA NIM', known: true, unlocks: ['text', 'vision'], signupUrl: 'https://build.nvidia.com' },
  // NOT measured. Declared so the user can see the gap and investigate it; the
  // pie renders these grey until an Investigate run fills them in.
  { provider: 'openrouter', label: 'OpenRouter', known: false, unlocks: [], signupUrl: 'https://openrouter.ai/keys' },
  { provider: 'z.ai', label: 'Z.ai', known: false, unlocks: [], signupUrl: 'https://z.ai' },
  { provider: 'mistral', label: 'Mistral', known: false, unlocks: [], signupUrl: 'https://console.mistral.ai' },
  { provider: 'cerebras', label: 'Cerebras', known: false, unlocks: [], signupUrl: 'https://cloud.cerebras.ai' },
];

/**
 * Turn declarations into slots against what is actually connected.
 *
 * A provider that has live models is `connected`. Everything else is
 * `not_connected` and appears as a grey slice with an Investigate button.
 */
export function buildSlots(
  declarations: ProviderDeclaration[],
  connected: string[],
  findings: Record<string, ResearchFinding | undefined> = {},
): ProviderSlot[] {
  return declarations.map((d) => {
    const finding = findings[d.provider];
    const status: SlotStatus = connected.includes(d.provider) ? 'connected' : 'not_connected';
    return {
      provider: d.provider,
      label: d.label,
      status,
      unlocks: finding?.unlocks?.length ? finding.unlocks : d.unlocks,
      note: finding?.summary ?? d.note ?? (d.known ? undefined : 'not yet investigated'),
    };
  });
}

// ── 1. the power-up lever ───────────────────────────────────────────────────

export interface PowerUpOffer {
  capability: CapabilityName;
  /** A PAID model, by name. Not measured here — this is a catalogue entry the
   *  user is choosing to spend on, and pretending we probed it would be the
   *  exact lie this dispatch forbids. */
  paidModel: string;
  provider: string;
  /** USD per million tokens, as a stated estimate. */
  estimatedUsdPerMTokens: number;
  rationale: string;
}

export interface OverrideRecord {
  capability: CapabilityName;
  paidModel: string;
  provider: string;
  /** The dispatch's 24 hours, explicit. */
  expiresAt: number;
  createdAt: number;
  estimatedUsdPerMTokens: number;
}

export const POWER_UP_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * Write the override. It is a graph node with an EXPIRY, not a preference: a
 * Power Up that quietly persisted forever would turn a one-day experiment into
 * an unbounded bill, and nothing in the UI would say so.
 */
export function powerUpKey(provider: string, model: string): string {
  return `override.${provider}.${model.replace(/[^a-zA-Z0-9._-]/g, '_')}`;
}

export function makeOverride(offer: PowerUpOffer, now: number): OverrideRecord {
  return {
    capability: offer.capability,
    paidModel: offer.paidModel,
    provider: offer.provider,
    createdAt: now,
    expiresAt: now + POWER_UP_WINDOW_MS,
    estimatedUsdPerMTokens: offer.estimatedUsdPerMTokens,
  };
}

/** Is this override still in force? An expired one must not route. */
export function overrideActive(o: OverrideRecord, now: number): boolean {
  return now < o.expiresAt;
}

export function overrideNodeValue(o: OverrideRecord, now: number): Record<string, unknown> {
  return {
    ...o,
    active: overrideActive(o, now),
    remainingMs: Math.max(0, o.expiresAt - now),
    windowHours: POWER_UP_WINDOW_MS / 3_600_000,
  };
}

// ── 2. the investigate loop ─────────────────────────────────────────────────

export interface ResearchFinding {
  provider: string;
  hasFreeTier: boolean | null;
  models: string[];
  unlocks: CapabilityName[];
  summary: string;
  sourceUrl: string;
  /** True when a model was asked; false when the loop could not run. */
  viaModel: boolean;
  at: number;
}

/** What the UI hands the pipeline. Provider-agnostic on purpose. */
export interface ResearchRunner {
  /**
   * Fetch a public page and ask a MODEL to read it. The dispatch is explicit:
   * the research loop must not run natively.
   */
  run: (input: { provider: string; url: string }) => Promise<ResearchFinding>;
}

export function researchKey(provider: string): string {
  return `research.${provider}`;
}

export interface ResearchOutcome {
  finding: ResearchFinding;
  /** Why the loop could not run, when it could not. */
  error?: string;
}

/**
 * Run one investigation and shape it for the graph.
 *
 * The failure path matters more than the happy one: no runner configured, a
 * fetch that returns nothing, a model that answers with prose instead of JSON —
 * all of them produce a finding with `viaModel: false` and a reason, never a
 * silent success and never an invented answer.
 */
export async function investigate(
  declaration: ProviderDeclaration,
  runner: ResearchRunner | null,
  now: number,
): Promise<ResearchOutcome> {
  if (!runner) {
    return {
      finding: {
        provider: declaration.provider, hasFreeTier: null, models: [], unlocks: [],
        summary: 'no pipeline runner configured', sourceUrl: declaration.signupUrl ?? '',
        viaModel: false, at: now,
      },
      error: 'no runner',
    };
  }
  try {
    const url = declaration.signupUrl ?? '';
    const finding = await runner.run({ provider: declaration.provider, url });
    return { finding: { ...finding, at: now } };
  } catch (e) {
    return {
      finding: {
        provider: declaration.provider, hasFreeTier: null, models: [], unlocks: [],
        summary: `investigation failed: ${e instanceof Error ? e.message : String(e)}`,
        sourceUrl: declaration.signupUrl ?? '', viaModel: false, at: now,
      },
      error: String(e),
    };
  }
}

/** Parse a model's reply into a finding. Tolerant of prose, strict about claims. */
export function parseResearch(
  provider: string,
  raw: string,
  sourceUrl: string,
  now: number,
): ResearchFinding {
  const jsonMatch = raw.match(/\{[\s\S]*\}/);
  if (!jsonMatch) {
    return {
      provider, hasFreeTier: null, models: [], unlocks: [],
      summary: raw.slice(0, 200), sourceUrl, viaModel: false, at: now,
    };
  }
  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(jsonMatch[0]);
  } catch {
    return {
      provider, hasFreeTier: null, models: [], unlocks: [],
      summary: 'model replied with prose, not JSON', sourceUrl, viaModel: false, at: now,
    };
  }
  const free = parsed.hasFreeTier;
  return {
    provider,
    // `true`/`false` only. A model that said "it depends" must not become true.
    hasFreeTier: typeof free === 'boolean' ? free : null,
    models: Array.isArray(parsed.models) ? (parsed.models as unknown[]).filter((m): m is string => typeof m === 'string') : [],
    unlocks: Array.isArray(parsed.unlocks) ? (parsed.unlocks as unknown[]).filter((u): u is CapabilityName =>
      ['text', 'vision', 'embeddings', 'reasoning'].includes(u as string)) : [],
    summary: typeof parsed.summary === 'string' ? parsed.summary : '',
    sourceUrl,
    viaModel: true,
    at: now,
  };
}